import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import styled from 'styled-components';

import { StudentColors } from 'components/pages/StudentDetail/parts/studentColors';
import type {
  DebtFilter,
  IRosterFilterState,
  IStudentListRow,
  LmsFilter,
  StatusFilter,
} from 'core/utils/studentRoster';

interface IStudentRosterFiltersProps {
  /** The full loaded roster — drives the distinct group/currency option lists. */
  rows: IStudentListRow[];
  filter: IRosterFilterState;
  onChange: (next: IRosterFilterState) => void;
}

const DEBT_OPTIONS: DebtFilter[] = ['any', 'has', 'none'];
const STATUS_OPTIONS: StatusFilter[] = ['any', 'active', 'terminated'];
const LMS_OPTIONS: LmsFilter[] = ['any', 'blocked', 'debtorsNoBlock'];

/**
 * Roster filter bar for the Students list (Area 1, Req 3).
 *
 * Four dimensions — debt status, agreement status, group, currency — each
 * mutually exclusive within itself (chip groups / selects) and AND-combined
 * across dimensions by `filterRoster`. Group and currency option lists are
 * derived from the loaded roster's distinct values. Every label routes through
 * i18next (Req 3, 4.5, 22); colors come from `StudentColors` (Req 17).
 */
export const StudentRosterFilters = memo(
  ({ rows, filter, onChange }: IStudentRosterFiltersProps) => {
    const { t } = useTranslation();

    const groupOptions = useMemo(
      () =>
        Array.from(
          new Set(rows.map((r) => r.groupName).filter(Boolean)),
        ).sort((a, b) => a.localeCompare(b)),
      [rows],
    );

    const currencyOptions = useMemo(
      () =>
        Array.from(
          new Set(rows.map((r) => r.currency).filter(Boolean)),
        ).sort((a, b) => a.localeCompare(b)),
      [rows],
    );

    const hasActiveFilter =
      filter.debt !== 'any' ||
      filter.status !== 'any' ||
      filter.lms !== 'any' ||
      filter.group !== null ||
      filter.currency !== null;

    return (
      <Bar>
        <Group role="group" aria-label={t('pages.students.filters.debt.label')}>
          {DEBT_OPTIONS.map((opt) => (
            <Chip
              key={opt}
              type="button"
              active={filter.debt === opt}
              onClick={() => onChange({ ...filter, debt: opt })}
            >
              {t(`pages.students.filters.debt.${opt}`)}
            </Chip>
          ))}
        </Group>

        <Group
          role="group"
          aria-label={t('pages.students.filters.status.label')}
        >
          {STATUS_OPTIONS.map((opt) => (
            <Chip
              key={opt}
              type="button"
              active={filter.status === opt}
              onClick={() => onChange({ ...filter, status: opt })}
            >
              {t(`pages.students.filters.status.${opt}`)}
            </Chip>
          ))}
        </Group>

        <Group role="group" aria-label={t('pages.students.filters.lms.label')}>
          {LMS_OPTIONS.map((opt) => (
            <Chip
              key={opt}
              type="button"
              active={filter.lms === opt}
              onClick={() => onChange({ ...filter, lms: opt })}
            >
              {t(`pages.students.filters.lms.${opt}`)}
            </Chip>
          ))}
        </Group>

        <Select
          aria-label={t('pages.students.filters.group.label')}
          value={filter.group ?? ''}
          onChange={(e) =>
            onChange({ ...filter, group: e.target.value || null })
          }
        >
          <option value="">{t('pages.students.filters.group.any')}</option>
          {groupOptions.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </Select>

        <Select
          aria-label={t('pages.students.filters.currency.label')}
          value={filter.currency ?? ''}
          onChange={(e) =>
            onChange({ ...filter, currency: e.target.value || null })
          }
        >
          <option value="">{t('pages.students.filters.currency.any')}</option>
          {currencyOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>

        {hasActiveFilter ? (
          <ClearButton
            type="button"
            onClick={() =>
              onChange({
                debt: 'any',
                status: 'any',
                lms: 'any',
                group: null,
                currency: null,
              })
            }
          >
            <X size={14} strokeWidth={2} />
            <span>{t('pages.students.filters.clear')}</span>
          </ClearButton>
        ) : null}
      </Bar>
    );
  },
);

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const Group = styled.div`
  display: inline-flex;
  border: 1px solid ${StudentColors.border};
  border-radius: 8px;
  overflow: hidden;
`;

const Chip = styled.button<{ active: boolean }>`
  border: none;
  background: ${({ active }) =>
    active ? StudentColors.accent : StudentColors.surface};
  color: ${({ active }) =>
    active ? StudentColors.surface : StudentColors.textSecondary};
  font: inherit;
  font-size: 13px;
  padding: 6px 12px;
  cursor: pointer;

  & + & {
    border-left: 1px solid ${StudentColors.border};
  }
`;

const Select = styled.select`
  border: 1px solid ${StudentColors.border};
  border-radius: 8px;
  background: ${StudentColors.surface};
  color: ${StudentColors.textSecondary};
  font: inherit;
  font-size: 13px;
  padding: 6px 10px;
  cursor: pointer;
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: ${StudentColors.accent};
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 4px;
`;
